/**
 * AdjustMixPanel — Results page interactive re-sizer.
 *
 * Lets the buyer scale solar, battery, wind and gensets, toggle diesel on/off,
 * and (on-/weak-grid only) set a grid-import share. On "Recalculate" it POSTs
 * to /api/configurator/recompute and reports the adjusted result upstream so
 * the parent Results page can show a side-by-side compare.
 *
 * Props:
 *   input              — the original configurator input doc
 *   recommended        — the recommended engine result (untouched baseline)
 *   onAdjusted(result) — callback invoked with the adjusted result payload
 *   language           — "en" | "fr"
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sliders, Zap, Battery, Fuel, Loader2, RotateCcw, Undo2, Redo2 } from "lucide-react";
import { track } from "@/lib/analytics";
import { buildWhySizeParts, WhySizePopover } from "@/lib/whySize";
import { co2IntensityGkWh } from "@/lib/mixMetrics";
import { firmBasisNote } from "@/lib/firmBasis";
import { seedThermalPicks, THERMAL_TECHS, DEFAULT_UNIT_MW_BY_TECH,
         recommendedThermalPlant, plantLabel } from "@/lib/thermalSeed";
import { firmTileOk } from "@/lib/firmPill";
import { num, money, moneyM, pct as pctFmt, unit, perUnit, perYr, parseNum } from "@/lib/numfmt";

const GRID_FIRST_DISPATCH = false;  // POST-LAUNCH: grid-first dispatch mode

const isNum = (v) => typeof v === "number" && !Number.isNaN(v);

// iter45i — Slider with an editable value input. Hoisted to module scope
// so React keeps the same component reference across parent re-renders;
// otherwise typing in the number input would lose focus after every
// keystroke because the parent recreates the closure on each `setState`.
const Slider = ({ testid, label, unit, value, bounds, step, onChange, icon: Icon, disabled = false }) => {
  const clamp = (n) => {
    if (!isNum(n)) return bounds.min;
    return Math.min(bounds.max, Math.max(bounds.min, n));
  };
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2 text-sm" style={{ color: "var(--char)" }}>
          {Icon && <Icon size={14} style={{ color: "var(--brand-green)" }} />}
          <span className="font-medium">{label}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <input
            type="number"
            min={bounds.min}
            max={bounds.max}
            step={step}
            value={isNum(value) ? Math.round(value * 100) / 100 : ""}
            disabled={disabled}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === "") { onChange(bounds.min); return; }
              onChange(clamp(Number(raw)));
            }}
            className="font-mono text-sm w-24 px-2 py-0.5 rounded border text-right"
            style={{ color: "var(--brand-green)", borderColor: "var(--rule)",
                      background: "var(--white)",
                      opacity: disabled ? 0.5 : 1 }}
            data-testid={`${testid}-value`}
          />
          <span className="font-mono text-sm" style={{ color: "var(--brand-green)" }}>
            {unit}
          </span>
        </div>
      </div>
      <input
        type="range"
        min={bounds.min}
        max={bounds.max}
        step={step}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-[var(--brand-green)]"
        style={{ opacity: disabled ? 0.4 : 1 }}
        data-testid={`${testid}-input`}
      />
    </div>
  );
};


const FUEL_UNAVAILABLE_REASON = {
  diesel_none: {
    en: "Not offered — diesel is marked unavailable at this site.",
    fr: "Non proposé — le diesel est indiqué comme indisponible sur ce site.",
  },
  diesel_constrained: {
    en: "Not offered — diesel at this site is marked black-start only (no regular supply).",
    fr: "Non proposé — le diesel est indiqué en démarrage autonome uniquement sur ce site (pas d'approvisionnement régulier).",
  },
  gas_none: {
    en: "Not offered — gas is marked unavailable at this site.",
    fr: "Non proposé — le gaz est indiqué comme indisponible sur ce site.",
  },
};

const GroupTitle = ({ testid, text, icon: Icon, className = "" }) => (
  <div className={`text-xs font-semibold uppercase tracking-wider mb-3 pb-2 flex items-center gap-1.5 ${className}`}
       style={{ color: "var(--brand-green)", borderBottom: "1px solid var(--rule)" }} data-testid={testid}>
    <Icon size={12} /> {text}
  </div>
);

const FuelUnavailableRow = ({ testid, label, reason }) => (
  <div className="col-span-1 md:col-span-2 flex items-center gap-3 flex-wrap" data-testid={testid}>
    <label className="flex items-center gap-2 text-sm" style={{ opacity: 0.5, cursor: "not-allowed" }}>
      <input type="checkbox" checked={false} disabled readOnly data-testid={`${testid}-toggle`} />
      <Fuel size={14} />
      <span style={{ color: "var(--char)" }}>{label}</span>
    </label>
    <span className="text-xs" style={{ color: "var(--muted)" }} data-testid={`${testid}-reason`}>{reason}</span>
  </div>
);

export const AdjustMixPanel = ({ input, recommended, onAdjusted, language = "en", applyRequest = null, onSaveConfig = null, savedAt = null, runId = null, projectId = null, usePlantRequest = null }) => {
  const rec = recommended?.model || {};
  // STEP 5b — the diesel ladder is the panel's bounds (one author: the thermal
  // config, admin-overridable); the pre-fill is the size the ladder picks.
  const [ladder, setLadder] = useState({ diesel_kw: [20, 5000], max_units: 12 });
  useEffect(() => {
    api.get("/config").then((r) => {
      const d = r?.data || {};
      const lad = { ...(d.thermal_defaults?.unit_ladders || {}), ...(d.thermal_overrides?.unit_ladders || {}) };
      if (lad.diesel_kw && lad.max_units) setLadder(lad);
    }).catch(() => {});
  }, []);
  const ladderMin = Math.min(...ladder.diesel_kw);
  const ladderMax = Math.max(...ladder.diesel_kw);
  const ladderPickKw = Number(rec.genset_unit_size_kw) || Number(
    (recommended?.candidates?.scoreboard || []).find((c) => c.kind === "diesel_only_reliable")?.sizing?.genset_unit_size_kw) || ladderMin;
  const gridStatus = input?.grid_status || "off_grid";
  // Launch C (RULED Jun 2026): the plant runs independently and the grid is backup only
  // (dispatch.py:16), so a grid-import share changes nothing; hidden until grid-first dispatch.
  const showGridSlider = GRID_FIRST_DISPATCH && gridStatus !== "off_grid";

  // Thermal picks the buyer selected on the wizard land in the recommended
  // result under model.thermal_extras.picks. If any, we surface a compact
  // count editor so Recalculate can re-scope thermal without leaving the
  // Results page — the same iter42 Δ-badge pattern lights up on the
  // adjusted thermal tiles once they've changed.
  const recThermalPicks = rec?.thermal_extras?.picks || [];
  const wizardFuelGas = input?.thermal_selection?.fuel_gas || {};
  // iter44i — Show the thermal editor when either the wizard picked thermal
  // OR the wizard exposed thermal options and gas is available (pipeline,
  // virtual-pipeline CNG, LNG, or LPG). This lets a buyer enable a gas
  // option from Results even when the wizard's optimiser didn't pick it —
  // matches the customer spec "show gas/thermal options only when gas is
  // available (or a virtual-pipeline option is selected)".
  // A fuel the site's inputs mark unavailable is never offered (shown disabled with the reason).
  const gasBlocked = String(input?.gas_availability || "").toLowerCase() === "none";
  const dieselAvail = String(input?.diesel_availability || "available").toLowerCase();
  const dieselBlocked = dieselAvail === "none"
    || (dieselAvail === "constrained" && rec.genset_role !== "blackstart");
  const gasAvailable = !gasBlocked && Boolean(wizardFuelGas.available || wizardFuelGas.source === "cng");
  const hasThermal = !gasBlocked && (recThermalPicks.length > 0 || gasAvailable);

  // D3 — the seeding gate is narrower than the fold gate: only a genuine
  // full-thermal WINNER means "the thermal plant is the recommendation", and
  // only then does the editor open on the recommended sizing rather than on
  // the wizard's step-7 picks.
  const fullThermalRecommendation = Boolean(
    recommended?.scored_candidate?.kind === "full_thermal"
    || recommended?.candidates?.chosen_kind === "full_thermal"
    || recommended?.scenarios?.winner_is_full_thermal
  );
  const recommendedPlant = recommendedThermalPlant(rec);

  // Default unit sizes when a tech isn't in the wizard's picks — kept
  // conservative so a "toggle-on" from scratch produces a plausible bar
  // for the buyer to further tune. Buyer can always override MW/unit.
  // D3 — authored by `lib/thermalSeed` so the editor, Reset and the
  // same-plant test read one definition.
  const DEFAULT_UNIT_MW = DEFAULT_UNIT_MW_BY_TECH;
  const ALL_THERMAL_TECHS = THERMAL_TECHS;  // CHP is a mode, not a tech

  // Prompt K §3 — the hybrid's gas-engine bank. Keyed on `gas_kw`, NOT on
  // `gas_units`: an OCGT / CCGT winner also carries a unit count, and this row
  // edits a reciprocating bank only.
  const showGasBank = !gasBlocked && (rec.gas_kw || 0) > 0 && (rec.gas_units || 0) > 0
                      && (rec.gas_unit_size_kw || 0) > 0;

  // One author for the seed so Reset restores the gas bank too, and the
  // gas_engine_* keys exist ONLY when the row that owns them is on screen.
  const initialOverrides = () => ({
    solar_kw: rec.solar_kw || 0,
    battery_kwh: rec.battery_kwh || 0,
    battery_power_kw: rec.battery_power_kw || 0,
    wind_kw: rec.wind_kw || 0,
    wind_on: (rec.wind_kw || 0) > 0,
    diesel_on: !dieselBlocked && (rec.genset_kw || 0) > 0,
    genset_units: rec.genset_units || 0,
    genset_unit_size_kw: ladderPickKw,
    ...(rec.genset_role && { genset_role: rec.genset_role }),
    grid_import_share: 0,
    ...(showGasBank && {
      gas_engine_units: rec.gas_units,
      gas_engine_unit_size_kw: rec.gas_unit_size_kw,
    }),
  });

  const [overrides, setOverrides] = useState(initialOverrides);
  // Separate state so thermal changes don't invalidate the main overrides
  // shape (which the recompute endpoint validates strictly). Populated
  // from the wizard's picks; empty when thermal wasn't used.
  const [thermalPicks, setThermalPicks] = useState(() => seedThermalPicks({
    model: rec, wizardPicks: [], isFullThermal: fullThermalRecommendation,
  }));
  // iter44i — gas price + CHP toggle live in the thermal block on the Adjust
  // panel so buyers can dry-run fuel exposure without leaving Results.
  // Seeded from the wizard snapshot; empty gas price → engine falls back to
  // admin default.
  const [gasPrice, setGasPrice] = useState(
    wizardFuelGas.delivered_price_per_gj ?? 8.0
  );
  const [chpEnabled, setChpEnabled] = useState(
    Boolean(input?.thermal_selection?.chp?.enabled)
  );
  const [busy, setBusy] = useState(false);
  const [warnings, setWarnings] = useState([]);

  const peak = Number(input?.peak_kw || 100);
  const bounds = useMemo(() => ({
    solar_kw:      { min: 0, max: Math.max(peak * 4, 50) },
    battery_kwh:   { min: 0, max: Math.max(peak * 12, 200) },
    battery_power_kw: { min: 0, max: Math.max(peak * 2, 50) },
    wind_kw:       { min: 0, max: Math.max(peak * 2, 50) },
    // Prompt K §8.4 — these bounds now match the backend clamp in
    // engine_overrides (DIESEL_MAX_UNITS / DIESEL_*_UNIT_KW). The HTML `max`
    // only limits the spinner, so `update` clamps typed values too.
    genset_units:  { min: 0, max: ladder.max_units },
    genset_unit_size_kw: { min: ladderMin, max: ladderMax },
    grid_import_share: { min: 0, max: 1 },
  }), [peak, ladder, ladderMin, ladderMax]);

  const update = (k, v) => setOverrides((o) => ({ ...o, [k]: v }));

  // iter45j — Battery kWh ⇄ kW coupling. Default assumption is a 4-hour
  // discharge, matching how the engine sizes battery power off capacity.
  // While the coupling is live, typing (or dragging) a new kWh also
  // updates kW = kWh / 4. The moment the buyer touches kW directly the
  // coupling breaks; a "Sync to 4-h" chip appears next to Battery power
  // so they can snap the ratio back with one click. Reset re-couples.
  const DEFAULT_DISCHARGE_H = 4;
  const isPowerCoupledRef = useRef(true);
  // D3 also-check — the chip is about the BUYER having broken the coupling,
  // not about the ratio. The recommended battery is often not 4-h (7.8 h on
  // the reported case), so a derived `kw === kwh/4` test offered "Sync to 4-h"
  // on a plant nobody had touched.
  const [powerTouched, setPowerTouched] = useState(false);
  const setBatteryKwh = (v) => {
    setOverrides((o) => {
      const next = { ...o, battery_kwh: v };
      if (isPowerCoupledRef.current) {
        // Round to the same 10 kW step used by the power slider so the
        // slider thumb sits on a valid tick.
        next.battery_power_kw = Math.max(0, Math.round((v / DEFAULT_DISCHARGE_H) / 10) * 10);
      }
      return next;
    });
  };
  const setBatteryPowerKw = (v) => {
    setPowerTouched(true);
    // Buyer manually touched the kW field/slider → break the coupling.
    // If they land exactly on kWh / 4 anyway, we treat it as still-in-sync.
    setOverrides((o) => {
      const target = Math.round((o.battery_kwh / DEFAULT_DISCHARGE_H) / 10) * 10;
      isPowerCoupledRef.current = (v === target);
      return { ...o, battery_power_kw: v };
    });
  };
  const resyncBatteryPower = () => {
    isPowerCoupledRef.current = true;
    setPowerTouched(false);
    setOverrides((o) => ({
      ...o,
      battery_power_kw: Math.max(0, Math.round((o.battery_kwh / DEFAULT_DISCHARGE_H) / 10) * 10),
    }));
  };

  const reset = () => {
    isPowerCoupledRef.current = true;  // iter45j — re-couple on reset
    setPowerTouched(false);
    setOverrides(initialOverrides());
    setThermalPicks(seedThermalPicks({
      model: rec, wizardPicks: [], isFullThermal: fullThermalRecommendation,
    }));
    setGasPrice(wizardFuelGas.delivered_price_per_gj ?? 8.0);
    setChpEnabled(Boolean(input?.thermal_selection?.chp?.enabled));
    setWarnings([]);
    setUndoStack([]); setRedoStack([]);  // iter44q/r — Reset also clears the undo/redo history
    onAdjusted?.(null, null);
  };

  const recalc = async () => {
    setBusy(true);
    try {
      // Zero out any tech the buyer toggled off so the backend engine sees
      // "remove that technology" — mirrors the diesel_on contract in
      // engine_overrides.py (iter44j — wind_on twin).
      const effectiveOverrides = {
        ...overrides,
        wind_kw: overrides.wind_on ? overrides.wind_kw : 0,
      };
      // Preserve the buyer's wizard-level thermal choices unless they
      // explicitly zeroed them here. Passing null when there are no picks
      // is the additive contract that keeps solar-only recomputes bit-
      // identical (see iter43 no-op invariance tests).
      const positivePicks = thermalPicks.filter((p) => Number(p.count) > 0);
      // iter44i — thermal_selection includes the buyer's live gas price and
      // CHP toggle from the panel (not just the wizard snapshot) so the
      // recompute reflects on-page edits. When no picks are active, we
      // send null so the additive no-op invariance still holds.
      let thermal_selection = null;
      if (positivePicks.length) {
        const wizardTS = input?.thermal_selection || {};
        const fg = wizardTS.fuel_gas || {};
        thermal_selection = {
          picks: positivePicks.map((p) => ({ tech: p.tech, unit_mw: Number(p.unit_mw), count: Number(p.count) })),
          fuel_gas: { ...fg, delivered_price_per_gj: Number(gasPrice) },
          include_in_mix: true,  // One panel (RULED): the thermal group IS part of "Your configuration".
          ...(wizardTS.site_thermal && { site_thermal: wizardTS.site_thermal }),
          ...(wizardTS.ambient && { ambient: wizardTS.ambient }),
          chp: {
            ...(wizardTS.chp || {}),
            enabled: chpEnabled,
            // Default host to the first positive pick when the wizard didn't
            // set one — engine needs a host to apply the uplift + credit.
            host_tech: (wizardTS.chp && wizardTS.chp.host_tech) || positivePicks[0].tech,
          },
        };
      }
      const r = await api.post("/configurator/recompute", { input, overrides: effectiveOverrides, thermal_selection, run_id: runId, project_id: projectId });
      const adjusted = r.data.result;
      setWarnings(adjusted?.notes?.guardrail_warnings || []);
      onAdjusted?.(adjusted);
      track("mix_adjusted", {
        solar: overrides.solar_kw, battery: overrides.battery_kwh,
        diesel_on: overrides.diesel_on, wind_on: overrides.wind_on,
        grid_share: overrides.grid_import_share,
        thermal_picks: positivePicks.length,
        gas_price: gasPrice, chp_on: chpEnabled,
      });
      toast.success(language === "fr" ? "Configuration recalculée" : "Configuration recalculated");
    } catch (e) {
      toast.error(language === "fr" ? "Impossible de recalculer" : "Could not recompute");
    } finally {
      setBusy(false);
    }
  };

  // iter44p — Apply an incoming carbon-tweak patch from Results.jsx and
  // auto-recalculate so the buyer sees the new score in one gesture.
  //   patch.batteryKwhAdd: number → adds to battery_kwh (+ scales battery_power_kw)
  //   patch.solarKwpAdd:   number → adds to solar_kw
  //   patch.chp:           bool   → flips chpEnabled on
  // We keep an id-based ref so consecutive identical tweaks still trigger.
  //
  // iter44q/44r — Track a bounded undo/redo stack (10 levels) so buyers
  // can walk back through several Try-it clicks step by step. Snapshots
  // capture the *exact* pre-tweak state including any hand-edits, so
  // Undo doesn't nuke earlier manual slider tweaks the way Reset would.
  const lastApplyIdRef = useRef(null);
  const UNDO_STACK_MAX = 10;
  const [undoStack, setUndoStack] = useState([]);   // most recent = tail
  const [redoStack, setRedoStack] = useState([]);   // most recent = tail
  const _snapshot = () => ({
    overrides: { ...overrides },
    chpEnabled: chpEnabled,
    at: Date.now(),
  });
  useEffect(() => {
    if (!applyRequest || applyRequest.id === lastApplyIdRef.current) return;
    lastApplyIdRef.current = applyRequest.id;
    const p = applyRequest.patch || {};
    // Push the pre-tweak snapshot onto the undo stack (capped at MAX).
    // A brand-new Try-it invalidates any pending Redo history — matches
    // the standard editor undo model (redo only lives on the "current
    // branch" of history).
    setUndoStack((cur) => {
      const next = [...cur, { ..._snapshot(), lever: applyRequest.lever || "unknown" }];
      return next.length > UNDO_STACK_MAX ? next.slice(next.length - UNDO_STACK_MAX) : next;
    });
    setRedoStack([]);
    setOverrides((cur) => {
      const next = { ...cur };
      if (typeof p.batteryKwhAdd === "number") {
        next.battery_kwh = Math.round((cur.battery_kwh || 0) + p.batteryKwhAdd);
        // Give the added capacity a proportional 4-hour discharge power
        // rating so the engine actually uses it — matches the sizing
        // convention on the wizard.
        next.battery_power_kw = Math.round((cur.battery_power_kw || 0) + p.batteryKwhAdd / 4);
      }
      if (typeof p.solarKwpAdd === "number") {
        next.solar_kw = Math.round((cur.solar_kw || 0) + p.solarKwpAdd);
      }
      return next;
    });
    if (p.chp === true) setChpEnabled(true);
    // Kick the recompute on the next tick so the state updates above have
    // committed (setState is async — reading `overrides` synchronously in
    // recalc would use the stale value).
    setTimeout(() => { recalc(); }, 30);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyRequest?.id]);

  // One panel (RULED): "Use this plant in my configuration" REPLACES the configuration with the
  // alternative plant — its units in the thermal group, renewables and diesel at zero. The plant's
  // black-start set comes with its gas bank on Recalculate (priced once), not as a diesel here.
  const panelRef = useRef(null);
  const lastPlantIdRef = useRef(null);
  useEffect(() => {
    if (!usePlantRequest || usePlantRequest.id === lastPlantIdRef.current) return;
    lastPlantIdRef.current = usePlantRequest.id;
    const { plant } = usePlantRequest;
    isPowerCoupledRef.current = true;
    setPowerTouched(false);
    setOverrides((cur) => {
      const next = { ...cur, solar_kw: 0, battery_kwh: 0, battery_power_kw: 0, wind_kw: 0, wind_on: false,
                     diesel_on: false, genset_units: 0, genset_unit_size_kw: cur.genset_unit_size_kw };
      delete next.genset_role;
      if ("gas_engine_units" in next) next.gas_engine_units = 0;
      return next;
    });
    setThermalPicks(THERMAL_TECHS.map((tech) => (tech === plant.tech
      ? { tech, unit_mw: Number(plant.unit_kw) / 1000, count: Number(plant.count), reasoning: "" }
      : { tech, unit_mw: DEFAULT_UNIT_MW_BY_TECH[tech], count: 0, reasoning: "" })));
    panelRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usePlantRequest?.id]);

  // iter44q/44r — Walk back one step through the undo stack. Pushes the
  // current state onto the redo stack so Redo can bring it back.
  const undoLastTweak = () => {
    if (undoStack.length === 0) return;
    const prev = undoStack[undoStack.length - 1];
    setRedoStack((cur) => {
      const next = [...cur, { ..._snapshot(), lever: prev.lever }];
      return next.length > UNDO_STACK_MAX ? next.slice(next.length - UNDO_STACK_MAX) : next;
    });
    setUndoStack((cur) => cur.slice(0, -1));
    setOverrides({ ...prev.overrides });
    setChpEnabled(prev.chpEnabled);
    track("mix_tweak_undo", { lever: prev.lever, depth: undoStack.length });
    setTimeout(() => { recalc(); }, 30);
  };

  // iter44r — Redo brings back the state we most recently undid. Pushes
  // the current state onto the undo stack so Undo can flip back again.
  const redoLastTweak = () => {
    if (redoStack.length === 0) return;
    const next = redoStack[redoStack.length - 1];
    setUndoStack((cur) => {
      const nx = [...cur, { ..._snapshot(), lever: next.lever }];
      return nx.length > UNDO_STACK_MAX ? nx.slice(nx.length - UNDO_STACK_MAX) : nx;
    });
    setRedoStack((cur) => cur.slice(0, -1));
    setOverrides({ ...next.overrides });
    setChpEnabled(next.chpEnabled);
    track("mix_tweak_redo", { lever: next.lever, depth: redoStack.length });
    setTimeout(() => { recalc(); }, 30);
  };
  // Prompt K §4 — the thermal editor EDITS THE WIZARD'S STEP-7 PICKS, which
  // drive the full-thermal alternative scenario and the adjusted thermal card,
  // NOT the mix sliders it used to sit under. The SAME JSX either way, so
  // state, focus, undo/redo and Save config stay in this one component; given
  // a slot it is portalled into its own card further down the page.
  const thermalEditorBody = (hasThermal || showGasBank) ? (
          <div className="col-span-1 md:col-span-2" data-testid="adjust-thermal-block">
            {/* D3 — on a full-thermal recommendation the editor opens on the
                RECOMMENDED plant, and says so, because the buyer's step-7
                picks are not what the engine recommended. */}
            {fullThermalRecommendation && recommendedPlant && (
              <div className="text-xs mb-2" style={{ color: "var(--muted)" }}
                   data-testid="thermal-seeded-from-recommended">
                {language === "fr"
                  ? `Initialisé sur la centrale recommandée\u00a0: ${plantLabel(recommendedPlant, "fr")}.`
                  : `Seeded from the recommended plant: ${plantLabel(recommendedPlant, "en")}.`}
              </div>
            )}
            <div className="rounded-md border" style={{ borderColor: "var(--rule)", background: "var(--white)" }}>
              {/* a06 Part 3 — the gas-engine bank is ONE row, in the thermal list, count × unit size like
                   the turbine rows, on every project (hybrid bank or full-thermal pick). */}
              {showGasBank && (
                <div className="flex items-center justify-between gap-3 p-3 flex-wrap" data-testid="adjust-gas-bank">
                  <div className="text-sm inline-flex items-center gap-1.5" style={{ color: "var(--char)" }}>
                    {language === "fr" ? "Moteurs à gaz" : "Gas engines"}
                  </div>
                  <div className="flex items-center gap-3 text-xs" style={{ color: "var(--muted)" }}>
                    <label className="flex items-center gap-1.5">
                      <span>{language === "fr" ? "Nombre" : "Count"}</span>
                      <input type="number" min={0} max={ladder.max_units} value={overrides.gas_engine_units ?? 0}
                              onChange={(e) => update("gas_engine_units", Math.min(ladder.max_units, Math.max(0, Number(e.target.value) || 0)))}
                              className="w-14 px-2 py-1 border rounded" style={{ borderColor: "var(--rule)" }}
                              data-testid="adjust-gas-bank-count" />
                    </label>
                    <label className="flex items-center gap-1.5">
                      <span>{language === "fr" ? "MW/unité" : "MW/unit"}</span>
                      <input type="number" min={0} step={0.05}
                              value={(Number(overrides.gas_engine_unit_size_kw) || 0) / 1000}
                              onChange={(e) => update("gas_engine_unit_size_kw", Math.max(0, Math.round((parseNum(e.target.value) || 0) * 1000)))}
                              className="w-20 px-2 py-1 border rounded" style={{ borderColor: "var(--rule)" }}
                              data-testid="adjust-gas-bank-size" />
                    </label>
                    <span className="font-mono" style={{ color: "var(--brand-green)" }} data-testid="adjust-gas-bank-total">
                      = {unit(Math.round((Number(overrides.gas_engine_units) || 0)
                                     * (Number(overrides.gas_engine_unit_size_kw) || 0)), "kW", 0)}
                    </span>
                  </div>
                </div>
              )}
              {thermalPicks.map((p, idx) => {
                // The hybrid's own gas-engine bank has its row above; this list adds turbines to it.
                if (showGasBank && p.tech === "gas_engine") return null;
                const label = p.tech === "gas_engine"
                  ? (language === "fr" ? "Moteurs à gaz" : "Gas engines")
                  : p.tech.toUpperCase();
                // iter44t + iter45f + iter45g — auto-recommend rationale
                // sits behind an info icon next to the tech label. The
                // shared `WhySizePopover` from `/app/frontend/src/lib/whySize.jsx`
                // paints the buyer note in green and the original auto
                // text in grey, matching the paid PDF character-for-
                // character.
                const recPick = recThermalPicks.find((rp) => rp.tech === p.tech);
                // Mirror the mapper's global flag: any thermal in the
                // recommended baseline means we can synthesise the buyer
                // note for a net-new tech (recPick undefined) too.
                const hasRecBaseline = recThermalPicks.length > 0;
                const parts = buildWhySizeParts(p, recPick, language, hasRecBaseline);
                const hasReasoning = Number(p.count) > 0
                  && (parts.buyerNotes.length > 0 || (parts.autoReasoning || "").trim());
                return (
                  <div key={p.tech}
                        className="flex items-center justify-between gap-3 p-3 flex-wrap"
                        style={{ borderTop: idx === 0 && !showGasBank ? "none" : "1px solid var(--rule)" }}
                        data-testid={`adjust-thermal-${p.tech}`}>
                    <div className="text-sm inline-flex items-center gap-1.5" style={{ color: "var(--char)" }}>
                      {label}
                      {hasReasoning && (
                        <WhySizePopover
                          testid={`adjust-thermal-${p.tech}-why`}
                          ariaLabel={language === "fr" ? "Pourquoi cette taille ?" : "Why this size?"}
                          picks={[{ label, buyerNotes: parts.buyerNotes, autoReasoning: parts.autoReasoning }]}
                          language={language}
                        />
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs" style={{ color: "var(--muted)" }}>
                      <label className="flex items-center gap-1.5">
                        <span>{language === "fr" ? "Nombre" : "Count"}</span>
                        <input type="number" min={0}
                                value={p.count}
                                onChange={(e) => setThermalPicks((ps) => ps.map((x, i) => i === idx ? { ...x, count: Number(e.target.value) } : x))}
                                className="w-14 px-2 py-1 border rounded"
                                style={{ borderColor: "var(--rule)" }}
                                data-testid={`adjust-thermal-${p.tech}-count`} />
                      </label>
                      <label className="flex items-center gap-1.5">
                        <span>{language === "fr" ? "MW/unité" : "MW/unit"}</span>
                        <input type="number" min={0.5} step={0.5}
                                value={p.unit_mw}
                                onChange={(e) => setThermalPicks((ps) => ps.map((x, i) => i === idx ? { ...x, unit_mw: Number(e.target.value) } : x))}
                                className="w-20 px-2 py-1 border rounded"
                                style={{ borderColor: "var(--rule)" }}
                                data-testid={`adjust-thermal-${p.tech}-size`} />
                      </label>
                    </div>
                  </div>
                );
              })}
              {/* CHP + delivered-gas-price row — sits in the same block so
                   buyers see all thermal knobs together. */}
              <div className="flex items-center justify-between gap-3 p-3 flex-wrap"
                    style={{ borderTop: "1px solid var(--rule)", background: "var(--paper)" }}
                    data-testid="adjust-thermal-controls">
                <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="adjust-chp-toggle">
                  <input type="checkbox" checked={chpEnabled}
                          onChange={(e) => setChpEnabled(e.target.checked)}
                          className="accent-[var(--brand-green)]" />
                  <span style={{ color: "var(--char)" }}>
                    {language === "fr" ? "CHP (cogénération)" : "CHP (combined heat + power)"}
                  </span>
                </label>
              </div>
              <div className="flex items-center justify-between gap-3 p-3 flex-wrap"
                    style={{ borderTop: "1px solid var(--rule)", background: "var(--paper)" }}
                    data-testid="adjust-thermal-gas-price-row">
                <label className="flex items-center gap-2 text-sm" style={{ color: "var(--char)" }}>
                  <span>{language === "fr" ? "Prix du gaz livré" : "Delivered gas price"}</span>
                  <input type="number" min={1} max={40} step={0.25} value={gasPrice}
                          onChange={(e) => setGasPrice(Number(e.target.value))}
                          className="w-20 px-2 py-1 border rounded"
                          style={{ borderColor: "var(--rule)" }}
                          data-testid="adjust-thermal-gas-price" />
                  <span className="font-mono">$/GJ</span>
                </label>
              </div>
            </div>
            <p className="text-xs mt-1.5" style={{ color: "var(--muted)" }}>
              {language === "fr"
                ? "Modifiez le nombre, la taille, le prix du gaz ou activez la cogénération pour voir l'impact sur la mise en service, le CAPEX et le coût carburant."
                : "Adjust count, size, gas price or toggle CHP to see the impact on time-to-COD, CAPEX and gas fuel cost."}
            </p>
          </div>
  ) : null;

  // §4 — count × MW against the firm target (peak × 1.20).
  const thermalBankKw = thermalPicks.reduce(
    (a, p) => a + (Number(p.count) || 0) * (Number(p.unit_mw) || 0) * 1000, 0);
  const firmTargetKw = peak * 1.20;
  const thermalOversized = firmTargetKw > 0 && thermalBankKw > firmTargetKw * 3;

  return (
    <>
    <section
      ref={panelRef}
      className="card p-6 md:p-8 mb-6"
      data-testid="adjust-mix-panel"
      style={{ background: "linear-gradient(135deg, #F6F5F2 0%, #EFEEDF 100%)", border: "1px solid var(--brand-green)" }}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <div className="overline" style={{ color: "var(--brand-green)" }}>
            <Sliders size={12} className="inline-block mr-1" />
            {language === "fr" ? "Ajuster le mix" : "Adjust the mix"}
          </div>
          <h3 className="font-display text-xl mt-1" style={{ color: "var(--char)" }} data-testid="adjust-mix-title">
            {language === "fr" ? "Votre configuration" : "Your configuration"}
          </h3>
          <p className="text-sm mt-1" style={{ color: "var(--muted)", maxWidth: 640 }} data-testid="adjust-mix-sub">
            {language === "fr"
              ? (recommended?.no_viable_candidate ? "Pré-rempli avec la centrale la plus proche. Modifiez" : "Pré-rempli avec la centrale recommandée. Modifiez")
                + " les tailles, puis recalculez pour comparer avec la recommandation\u00a0; Réinitialiser revient à la recommandation."
              : (recommended?.no_viable_candidate ? "Pre-filled with the closest plant. Change any size" : "Pre-filled with the recommended plant. Change any size")
                + ", then Recalculate to compare it with the recommendation; Reset returns to the recommendation."}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {/* iter44q/r — Undo/Redo chips only visible when their stacks
               are non-empty. Sit before Reset so a nervous buyer's
               rollback is the leftmost muscle memory. Each shows the
               stack depth in parens so buyers know how far they can
               walk back / forward. */}
          {undoStack.length > 0 && (
            <button
              onClick={undoLastTweak}
              className="!py-2 !px-3 text-sm rounded font-medium transition-colors inline-flex items-center gap-1.5"
              style={{ background: "#FEF3C7", color: "#873F00", border: "1px solid #F1C87A" }}
              data-testid="adjust-undo-btn"
              disabled={busy}
              title={language === "fr"
                ? `Annuler la dernière suggestion (${undoStack.length} niveau${undoStack.length > 1 ? "x" : ""})`
                : `Roll back the last Try-it tweak (${undoStack.length} level${undoStack.length > 1 ? "s" : ""})`}
            >
              <Undo2 size={14} />
              {language === "fr" ? "Annuler" : "Undo"}
              <span className="font-mono text-[10px] opacity-70">({undoStack.length})</span>
            </button>
          )}
          {redoStack.length > 0 && (
            <button
              onClick={redoLastTweak}
              className="!py-2 !px-3 text-sm rounded font-medium transition-colors inline-flex items-center gap-1.5"
              style={{ background: "#EAF3EE", color: "#1A6F2B", border: "1px solid #A7CFB4" }}
              data-testid="adjust-redo-btn"
              disabled={busy}
              title={language === "fr"
                ? `Rétablir (${redoStack.length} niveau${redoStack.length > 1 ? "x" : ""})`
                : `Redo (${redoStack.length} level${redoStack.length > 1 ? "s" : ""})`}
            >
              <Redo2 size={14} />
              {language === "fr" ? "Rétablir" : "Redo"}
              <span className="font-mono text-[10px] opacity-70">({redoStack.length})</span>
            </button>
          )}
          <button onClick={reset} className="btn-secondary !py-2 !px-3 text-sm" data-testid="adjust-reset-btn" disabled={busy}>
            <RotateCcw size={14} /> {language === "fr" ? "Réinitialiser" : "Reset"}
          </button>
          <button onClick={recalc} className="btn-primary !py-2 !px-4 text-sm" data-testid="adjust-recalc-btn" disabled={busy}>
            {busy ? <Loader2 size={14} className="animate-spin" /> : null}
            {language === "fr" ? "Recalculer" : "Recalculate"}
          </button>
          {/* iter45al — Save button. Persists override deltas + thermal_selection
              on the project; restore replays them through the current engine
              (inputs-persist / outputs-recompute). Only visible when a save
              handler is wired (i.e. the project has been saved). */}
          {onSaveConfig && (
            <button
              onClick={() => {
                // Same shape as recalc's payload
                const positivePicks = thermalPicks.filter(p => Number(p.count) > 0 && Number(p.unit_mw) > 0);
                let ts = null;
                if (positivePicks.length) {
                  const wizardTS = input?.thermal_selection || {};
                  const fg = wizardTS.fuel_gas || {};
                  ts = {
                    picks: positivePicks.map(p => ({ tech: p.tech, unit_mw: Number(p.unit_mw), count: Number(p.count) })),
                    fuel_gas: { ...fg, delivered_price_per_gj: Number(gasPrice) },
                    include_in_mix: true,
                    ...(wizardTS.site_thermal && { site_thermal: wizardTS.site_thermal }),
                    ...(wizardTS.ambient && { ambient: wizardTS.ambient }),
                    chp: { ...(wizardTS.chp || {}), enabled: chpEnabled,
                            host_tech: (wizardTS.chp && wizardTS.chp.host_tech) || positivePicks[0].tech },
                  };
                }
                onSaveConfig({ overrides, thermal_selection: ts });
              }}
              className="btn-secondary !py-2 !px-3 text-sm"
              data-testid="adjust-save-config-btn"
              disabled={busy}
              title={language === "fr" ? "Enregistrer votre configuration" : "Save your configuration"}
            >
              💾 {savedAt
                ? (language === "fr" ? "Enregistré" : "Saved")
                : (language === "fr" ? "Enregistrer" : "Save config")}
            </button>
          )}
        </div>
      </div>

      <GroupTitle testid="adjust-group-renewables" icon={Zap}
                  text={language === "fr" ? "Solaire, batterie et éolien" : "Solar, battery & wind"} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Slider testid="adjust-solar" label={language === "fr" ? "Solaire" : "Solar PV"} unit={language === "fr" ? "kWc" : "kWp"}
                value={overrides.solar_kw} bounds={bounds.solar_kw} step={10} icon={Zap}
                onChange={(v) => update("solar_kw", v)} />
        <Slider testid="adjust-battery-kwh" label={language === "fr" ? "Batterie (capacité)" : "Battery capacity"} unit="kWh"
                value={overrides.battery_kwh} bounds={bounds.battery_kwh} step={20} icon={Battery}
                onChange={setBatteryKwh} />
        {/* iter45j — Battery power sits below capacity. When the coupling
             is live, this field is auto-driven from capacity / 4. Touching
             the field breaks the coupling and reveals a "Sync 4-h" chip
             so the buyer can snap the ratio back with one click. The
             current discharge duration (hours) sits next to the chip so
             lenders see the ratio at a glance. */}
        <div>
          <Slider testid="adjust-battery-power" label={language === "fr" ? "Batterie (puissance)" : "Battery power"} unit="kW"
                    value={overrides.battery_power_kw} bounds={bounds.battery_power_kw} step={10} icon={Battery}
                    onChange={setBatteryPowerKw} />
          {(() => {
            const kwh = Number(overrides.battery_kwh || 0);
            const kw = Number(overrides.battery_power_kw || 0);
            if (kwh <= 0 || kw <= 0) return null;
            const hours = kwh / kw;
            const target = Math.max(0, Math.round((kwh / DEFAULT_DISCHARGE_H) / 10) * 10);
            const inSync = kw === target;
            const showSync = powerTouched && !inSync;
            const durLabel = language === "fr"
              ? `Durée : ${unit(hours, "h", 1, 1)}`
              : `Duration: ${unit(hours, "h", 1, 1)}`;
            return (
              <div className="flex items-center gap-2 mt-1.5 text-xs" data-testid="battery-coupling-hint">
                <span className="font-mono" style={{ color: showSync ? "#873F00" : "var(--brand-green)" }}>
                  {durLabel}
                </span>
                {showSync && (
                  <button type="button" onClick={resyncBatteryPower}
                          data-testid="battery-power-sync-btn"
                          className="px-2 py-0.5 rounded font-medium text-[10px] uppercase tracking-wider transition-colors"
                          style={{ background: "#E4F3E7", color: "var(--brand-green)", border: "1px solid #B9DFC1" }}>
                    {language === "fr" ? `Synchroniser à ${DEFAULT_DISCHARGE_H} h` : `Sync to ${DEFAULT_DISCHARGE_H}-h`}
                  </button>
                )}
              </div>
            );
          })()}
        </div>
        {/* iter44j + iter45i — wind gets an explicit on/off checkbox so
             buyers can zero it in one click. The Slider itself now
             renders the editable value + unit; we just add the checkbox
             above it. When off, the slider stays visible but disabled
             + greyed so the buyer sees what they toggled off. */}
        <div data-testid="adjust-wind">
          <label className="flex items-center gap-2 text-sm cursor-pointer mb-1" data-testid="adjust-wind-toggle">
            <input type="checkbox" checked={!!overrides.wind_on}
                    onChange={(e) => update("wind_on", e.target.checked)}
                    className="accent-[var(--brand-green)]" />
            <span className="font-medium" style={{ color: "var(--char)" }}>
              {language === "fr" ? "Éolien" : "Wind"}
            </span>
          </label>
          <Slider testid="adjust-wind-slider"
                    label=""
                    unit="kW"
                    value={overrides.wind_on ? overrides.wind_kw : 0}
                    bounds={bounds.wind_kw}
                    step={10}
                    disabled={!overrides.wind_on}
                    icon={Zap}
                    onChange={(v) => update("wind_kw", v)} />
        </div>
        {showGridSlider && (
          <Slider testid="adjust-grid-share" label={language === "fr" ? "Part réseau" : "Grid-import share"} unit="%"
                  value={Math.round(overrides.grid_import_share * 100)}
                  bounds={{ min: 0, max: 100 }} step={5}
                  onChange={(v) => update("grid_import_share", v / 100)} />
        )}
      </div>

      <GroupTitle testid="adjust-group-thermal" icon={Fuel} className="mt-8"
                  text={language === "fr" ? "Production thermique" : "Thermal generation"} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {dieselBlocked ? (
          <FuelUnavailableRow testid="adjust-diesel-unavailable"
                              label={language === "fr" ? "Groupes diesel" : "Diesel gensets"}
                              reason={FUEL_UNAVAILABLE_REASON[dieselAvail === "none" ? "diesel_none" : "diesel_constrained"][language === "fr" ? "fr" : "en"]} />
        ) : (
        <div className="col-span-1 md:col-span-2 flex items-center gap-4 flex-wrap"
              data-testid="adjust-genset">
          <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="adjust-diesel-toggle">
            <input type="checkbox" checked={overrides.diesel_on}
                    onChange={(e) => update("diesel_on", e.target.checked)}
                    className="accent-[var(--brand-green)]" />
            <Fuel size={14} style={{ color: "var(--brand-green)" }} />
            {/* Prompt K §2 — the hybrid's diesel is a black-start set. */}
            <span style={{ color: "var(--char)" }}>
              {rec.genset_role === "blackstart"
                ? (language === "fr" ? "Diesel de démarrage (secours)" : "Diesel black-start (standby)")
                : (language === "fr" ? "Groupes diesel" : "Diesel gensets")}
            </span>
          </label>
          {overrides.diesel_on && (
            <>
              <label className="text-sm flex items-center gap-2">
                <span style={{ color: "var(--muted)" }}>{language === "fr" ? "Nombre" : "Count"}</span>
                <input type="number" min={0} max={ladder.max_units} value={overrides.genset_units}
                        onChange={(e) => update("genset_units", Math.min(ladder.max_units, Math.max(0, Number(e.target.value) || 0)))}
                        className="w-16 px-2 py-1 border border-[var(--rule)] rounded" data-testid="adjust-genset-count" />
              </label>
              <label className="text-sm flex items-center gap-2">
                <span style={{ color: "var(--muted)" }}>{language === "fr" ? "Taille (kW)" : "Unit size (kW)"}</span>
                <input type="number" min={ladderMin} max={ladderMax} step={10} value={overrides.genset_unit_size_kw}
                        onChange={(e) => update("genset_unit_size_kw", Number(e.target.value))}
                        onBlur={(e) => update("genset_unit_size_kw", Math.min(ladderMax, Math.max(ladderMin, Number(e.target.value) || ladderMin)))}
                        className="w-20 px-2 py-1 border border-[var(--rule)] rounded" data-testid="adjust-genset-size" />
                <span className="text-xs" style={{ color: "var(--muted)" }} data-testid="adjust-genset-size-range">{num(ladderMin, 0)}–{unit(ladderMax, "kW", 0)}</span>
              </label>
            </>
          )}
        </div>
        )}
        {gasBlocked && (
          <FuelUnavailableRow testid="adjust-gas-unavailable"
                              label={language === "fr" ? "Moteurs à gaz et thermique" : "Gas engines & thermal"}
                              reason={FUEL_UNAVAILABLE_REASON.gas_none[language === "fr" ? "fr" : "en"]} />
        )}

        {/* Thermal count editor (iter44d/iter44i). Renders when the wizard
             already put thermal picks on the recommended result OR when the
             wizard exposed thermal options and gas is available (per the
             customer spec). Buyer can toggle any of the 3 gas techs on by
             setting count > 0. A CHP checkbox + delivered-gas-price input
             live inside the block so on-page edits recompute end-to-end
             from a single Recalculate click. */}
        {thermalEditorBody}
        {thermalOversized && (
          <div className="col-span-1 md:col-span-2 rounded text-sm" data-testid="thermal-editor-oversize"
                style={{ background: "#FDECD9", border: "1px solid #E58B25", padding: "8px 10px", color: "#873F00" }}>
            {language === "fr"
              ? `⚠ Ce banc représente ${num(thermalBankKw / firmTargetKw, 1, 1)}× l’objectif de capacité ferme de ${unit(Math.round(firmTargetKw), "kW", 0)} — vérifiez le nombre et la taille des unités.`
              : `⚠ This bank is ${num(thermalBankKw / firmTargetKw, 1, 1)}× the firm target of ${unit(Math.round(firmTargetKw), "kW", 0)} — check the unit count and size.`}
          </div>
        )}
      </div>

      {warnings.length > 0 && (
        <div className="mt-4 rounded" data-testid="adjust-warnings"
              style={{ background: "#FDECD9", border: "1px solid #E58B25", padding: "10px 12px", color: "#873F00" }}>
          <div className="text-xs font-bold mb-1">
            {language === "fr" ? "⚠ Attention" : "⚠ Guardrail warnings"}
          </div>
          <ul className="text-sm list-disc pl-5">
            {warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}
    </section>
    </>
  );
};


/**
 * MixCompare — Fix 4 (iter45aj) Layout C: metric-row + delta-chip overlay.
 *
 * Renders Recommended vs Your Configuration as a metric list. Δ chips fire
 * per-metric (thresholds per user spec below). Adequacy rows (Firm capacity,
 * Unmet load) render UNCONDITIONALLY — safe state must be visible, absence
 * would be misread as "not implemented" vs "clean."
 *
 * Δ chip direction convention:
 *   green ▼  — User is LOWER on a lower-is-better metric (CAPEX, OPEX, LCOE, CO2, unmet, TCO, payback)
 *   green ▲  — User is HIGHER on a higher-is-better metric (renewable, firm capacity, savings, CO2-avoided)
 *   amber ▼  — User is LOWER on a higher-is-better metric
 *   amber ▲  — User is HIGHER on a lower-is-better metric
 *
 * Per-metric thresholds (user-approved):
 *   CAPEX / TCO / OPEX : |Δ| ≥ $500k AND |Δ| ≥ 2%
 *   LCOE               : |Δ| ≥ $0.01/kWh
 *   CO2 intensity      : |Δ| ≥ 2%
 *   Renewable fraction : |Δ| ≥ 1 percentage point
 *   Firm / Unmet       : always shown, ANY nonzero delta (no floor)
 *   Payback / savings  : ≥$100k AND ≥ 5% (secondary metrics)
 */
export const MixCompare = ({ recommended, adjusted, language = "en", emissionFactors = null }) => {
  if (!adjusted) return null;

  const t = (en, fr) => language === "fr" ? fr : en;
  const rec = recommended || {};
  const usr = adjusted || {};

  // Adequacy state on the USER config — drives inline chip AND breach block.
  const userFirmAvail = Number(usr?.model?.firm_capacity_available_kw || 0);
  const userFirmTarget = Number(usr?.model?.firm_capacity_target_kw || 0);
  // Jun 2026 ruling — the pill rule is one author: a configuration that meets
  // its firm target but fails N-1 is NOT adequate, and "Your configuration"
  // must not read green on it either.
  const userFirmShort = userFirmTarget > 0 && (usr?.model?.firm_capacity_ok === false
                          || userFirmAvail < userFirmTarget * 0.98
                          || !firmTileOk(usr?.model || {}));
  const userUnmet = Number(usr?.model?.unmet_kwh_yr || 0);
  const userAnnual = Number(usr?.model?.annual_energy_kwh || 0);
  // Prompt K §5 — the unmet SHARE is a share of DEMAND. Dividing by served
  // energy reported "175,200 MWh/yr (0 % of annual demand)" on a plant that
  // served nothing: the denominator shrank with the numerator.
  const userDemand = Number(usr?.model?.annual_demand_kwh || 0)
                      || (Number(usr?.model?.annual_served_kwh || 0) + userUnmet);
  const userUnmetPct = userDemand > 0 ? (userUnmet / userDemand) * 100 : 0;
  const userUnmetTrips = userUnmet >= 1000;
  // A configuration with no generating plant has no CAPEX, TCO, OPEX or LCOE —
  // and $0.00M is a figure, not an absence.
  const userNoPlant = Number(usr?.capex?.total || 0) <= 0
                      && Number(usr?.model?.annual_served_kwh ?? userAnnual) <= 0;
  const notComputed = t("not computed — no generating plant",
                         "non calculé — aucune centrale");

  // D1 — authored by `lib/mixMetrics`. Reads the engine-published intensity
  // first, so MixCompare and the carbon card can never disagree.
  const co2Intensity = (r) => co2IntensityGkWh(r, emissionFactors);

  // Metric row builder. Each row declares direction ("lower" = lower is better)
  // and its own threshold predicate.
  const row = ({ label, get, fmt, direction, threshold, alwaysShow = false, testid,
                  inlineChip = null, costRow = false }) => {
    const rv = get(rec);
    const av = get(usr);
    const has = rv != null && av != null && !Number.isNaN(rv) && !Number.isNaN(av);
    const delta = has ? (av - rv) : null;
    const pctDelta = has && rv !== 0 ? (delta / Math.abs(rv)) * 100 : null;
    let show = alwaysShow || (has && threshold(delta, pctDelta, rv, av));
    let chipTone = null, arrow = null, chipText = null;
    if (costRow && userNoPlant) {
      return { label, rec: has ? fmt.val(rv) : "—", usr: notComputed,
                chipTone: null, arrow: null, chipText: null, show: true, testid,
                inlineChip: null };
    }
    if (has && (delta !== 0)) {
      const userBetter = direction === "lower" ? delta < 0 : delta > 0;
      chipTone = userBetter ? "green" : "amber";
      arrow = delta < 0 ? "▼" : "▲";
      chipText = fmt.chip ? fmt.chip(delta, pctDelta) : (
        pctDelta != null ? `${arrow} ${(pctDelta >= 0 ? "+" : "")}${pctFmt(pctDelta, 0)}`
                          : `${arrow} ${delta >= 0 ? "+" : ""}${delta}`
      );
      // §5-D — never a green LCOE badge while the plant is short.
      if (testid === "compare-row-lcoe" && chipTone === "green"
          && (userUnmetTrips || userFirmShort)) chipTone = "amber";
    }
    return { label, rec: has ? fmt.val(rv) : "—", usr: has ? fmt.val(av) : "—",
              chipTone, arrow, chipText, show, testid, inlineChip };
  };

  // Format helpers
  const usdM = (v) => `${moneyM(Number(v), 2)}`;
  const usdMyr = (v) => `${moneyM(Number(v), 2)}${perYr()}`;
  const pct = (v) => pctFmt(v, 0);
  // Sign BEFORE the currency symbol: −$29.70M, never $-29.70M. U+2212 for the
  // minus, matching the COI tiles' convention.
  const chipUsdM = (d, p) =>
    `${d < 0 ? "▼" : "▲"} ${d < 0 ? "−" : "+"}${moneyM(Math.abs(d), 2)} (${pct(p)})`;
  const chipUsdKwh = (d) =>
    `${d < 0 ? "▼" : "▲"} ${d < 0 ? "−" : "+"}${perUnit(Math.abs(d), "kWh", 3)}`;

  const rows = [
    row({
      label: "CAPEX",
      get: (r) => Number(r?.capex?.total || 0),
      fmt: { val: usdM, chip: chipUsdM },
      direction: "lower",
      threshold: (d, p) => Math.abs(d) >= 500_000 && Math.abs(p) >= 2,
      testid: "compare-row-capex",
      costRow: true,
    }),
    row({
      label: t("20-yr TCO", "TCO 20 ans"),
      // ONE TCO — the engine's 20-year cost, never recomputed here.
      get: (r) => Number(r?.financials?.tco_20yr_usd || 0),
      fmt: { val: usdM, chip: chipUsdM },
      direction: "lower",
      threshold: (d, p) => Math.abs(d) >= 500_000 && Math.abs(p) >= 2,
      testid: "compare-row-tco",
      costRow: true,
    }),
    row({
      label: t("OPEX (annual)", "OPEX (annuel)"),
      get: (r) => Number(r?.opex?.annual || 0),
      fmt: { val: usdMyr, chip: chipUsdM },
      direction: "lower",
      threshold: (d, p) => Math.abs(d) >= 500_000 && Math.abs(p) >= 2,
      testid: "compare-row-opex",
      costRow: true,
    }),
    row({
      label: "LCOE",
      get: (r) => Number(r?.financials?.lcoe_usd_per_kwh || 0),
      fmt: { val: (v) => `${perUnit(v, "kWh", 3)}`, chip: chipUsdKwh },
      direction: "lower",
      threshold: (d) => Math.abs(d) >= 0.01,
      testid: "compare-row-lcoe",
      costRow: true,
      // Prompt K §5-D — a plant that serves less energy can post a LOWER
      // LCOE simply because unmet energy has no cost. The shortfall rides on
      // the same row, so the number is never read as a clean improvement.
      inlineChip: (userUnmetTrips || userFirmShort)
        ? { tone: "amber",
            text: t(`⚠ excludes ${pct(userUnmetPct, 0)} unmet`,
                     `⚠ exclut ${pct(userUnmetPct, 0)} non servi`) }
        : null,
    }),
    row({
      label: t("Renewable fraction", "Fraction renouvelable"),
      get: (r) => (Number(r?.model?.renewable_fraction || 0) * 100),
      fmt: { val: pct,
              chip: (d) => `${d < 0 ? "▼" : "▲"} ${d >= 0 ? "+" : ""}${num(d, 0, 0)} pp` },
      direction: "higher",
      threshold: (d) => Math.abs(d) >= 1,
      testid: "compare-row-renewable",
    }),
    row({
      label: t("CO₂ intensity", "Intensité CO₂"),
      get: co2Intensity,
      fmt: { val: (v) => `${unit(v, "g/kWh", 0, 0)}`,
              chip: (d, p) => `${d < 0 ? "▼" : "▲"} ${d >= 0 ? "+" : ""}${num(d, 0)} g (${pct(p)})` },
      direction: "lower",
      threshold: (d, p) => Math.abs(p) >= 2,
      testid: "compare-row-co2",
    }),
    // -------- Adequacy rows: ALWAYS SHOWN, any nonzero delta --------
    row({
      label: t("Firm capacity", "Capacité ferme"),
      get: (r) => Number(r?.model?.firm_capacity_available_kw || 0),
      fmt: { val: (v) => `${unit(v, "kW", 3)}`,
              chip: (d) => `${d < 0 ? "▼" : "▲"} ${d >= 0 ? "+" : ""}${unit(d, "kW", 3)}` },
      direction: "higher",
      threshold: (d) => Math.abs(d) > 0,   // any nonzero
      alwaysShow: true,
      testid: "compare-row-firm",
      inlineChip: userFirmShort
        ? { tone: "red", text: t("⚠ SHORT", "⚠ INSUFFISANT") }
        : { tone: "green", text: "✓" },
    }),
    row({
      label: t("Unmet load", "Énergie non fournie"),
      get: (r) => Math.round(Number(r?.model?.unmet_kwh_yr || 0) / 1000),   // MWh/yr
      fmt: { val: (v) => `${unit(v, `MWh${perYr()}`, 3)}`,
              chip: (d) => `${d < 0 ? "▼" : "▲"} ${d >= 0 ? "+" : ""}${unit(d, `MWh${perYr()}`, 3)}` },
      direction: "lower",
      threshold: (d) => Math.abs(d) > 0,
      alwaysShow: true,
      testid: "compare-row-unmet",
      inlineChip: userUnmetTrips
        ? { tone: "amber", text: `⚠ ${pct(userUnmetPct, 0)}` }
        : { tone: "green", text: "✓" },
    }),
  ];

  const breaches = [];
  if (userFirmShort) breaches.push({
    label: t("Firm-capacity shortfall", "Capacité ferme insuffisante"),
    detail: `${unit(Math.round(userFirmAvail), "kW", 0)} < ${unit(Math.round(userFirmTarget), "kW", 0)} target`,
    testid: "compare-breach-firm",
  });
  if (userUnmetTrips) breaches.push({
    label: t("Unmet energy shortfall", "Déficit d'énergie"),
    detail: `${unit(Math.round(userUnmet/1000), `MWh${perYr()}`, 0)} (${pct(userUnmetPct)} of annual demand)`,
    testid: "compare-breach-unmet",
  });

  const chipStyle = (tone) => ({
    display: "inline-block", padding: "2px 8px", borderRadius: "999px",
    fontSize: "11px", fontWeight: 600, marginLeft: 8, whiteSpace: "nowrap",
    ...(tone === "green" ? { background: "#E8F5E9", color: "#1B5E20" } :
        tone === "amber" ? { background: "#FFF4E6", color: "#B45309" } :
        tone === "red"   ? { background: "#FDECEC", color: "#B00020" } :
        {}),
  });

  return (
    <section className="card p-6 md:p-8 mb-6" data-testid="mix-compare">
      <div className="overline mb-3">
        {t("Recommended vs your configuration", "Recommandé vs votre configuration")}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--rule)] text-left">
              <th className="py-2 pr-4 font-normal text-xs uppercase tracking-wider" style={{ color: "var(--muted)" }}>
                {t("Metric", "Métrique")}
              </th>
              <th className="py-2 pr-4 font-normal text-xs uppercase tracking-wider" style={{ color: "var(--muted)" }}>
                {t("Recommended", "Recommandé")}
              </th>
              <th className="py-2 pr-4 font-normal text-xs uppercase tracking-wider" style={{ color: "var(--brand-green)" }}>
                {t("Your configuration", "Votre configuration")}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.filter(r => r.show).map((r, i) => (
              <tr key={i} className="border-b border-[var(--rule)]" data-testid={r.testid || `compare-row-${i}`}>
                <td className="py-2 pr-4" style={{ color: "var(--char)" }}>{r.label}</td>
                <td className="py-2 pr-4" style={{ color: "var(--muted)" }}>{r.rec}</td>
                <td className="py-2 pr-4 font-medium" style={{ color: "var(--brand-green)" }}>
                  {r.usr}
                  {r.chipTone && r.chipText && (
                    <span style={chipStyle(r.chipTone)} data-testid={`${r.testid}-chip`}>
                      {r.chipText}
                    </span>
                  )}
                  {r.inlineChip && (
                    <span style={chipStyle(r.inlineChip.tone)} data-testid={`${r.testid}-adequacy`}>
                      {r.inlineChip.text}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* F3 RULED — the firm row is the AMBIENT-DERATED plant on both sides;
          the nameplate and the conditions are stated once, under the table. */}
      {(firmBasisNote(recommended?.model, language) || firmBasisNote(adjusted?.model, language)) && (
        <p className="text-xs mt-3" style={{ color: "var(--muted)", lineHeight: 1.5 }}
           data-testid="compare-firm-basis-note">
          {firmBasisNote(adjusted?.model, language) || firmBasisNote(recommended?.model, language)}
        </p>
      )}
      {breaches.length > 0 && (
        <div className="mt-4 p-4 rounded-md"
              style={{ background: "#FFF4E6", border: "1.5px solid #B45309" }}
              data-testid="compare-breach-summary">
          <div className="text-xs font-semibold mb-2" style={{ color: "#B45309" }}>
            {t(`⚠ ${breaches.length} guardrail breach${breaches.length > 1 ? "es" : ""} on Your Configuration`,
                `⚠ ${breaches.length} alerte${breaches.length > 1 ? "s" : ""} de garde-fou sur votre configuration`)}
          </div>
          <ul className="text-xs" style={{ color: "#7a4408" }}>
            {breaches.map((b) => (
              <li key={b.label} data-testid={b.testid} className="mb-1">
                • <strong>{b.label}:</strong> {b.detail}
              </li>
            ))}
            <li className="mt-1" style={{ opacity: 0.85 }}>
              {t("Recommended stays green on both.", "Recommandé reste vert sur les deux.")}
            </li>
          </ul>
        </div>
      )}
    </section>
  );
};

